import { Dialog, Menu, Transition } from '@headlessui/react';
import { useFormik } from 'formik';
import React from 'react';
import { Option } from 'react-multi-select-component/dist/lib/interfaces';
import Select from 'react-select';
import * as Yup from 'yup';

import { getDetailUsers } from '../../api/GET_detailUsers';
import { getOutlets } from '../../api/GET_oulets';
import { getUsers } from '../../api/GET_users';
import { postUpdateUsers } from '../../api/POST_updateUsers';
import { postUsers } from '../../api/POST_users';
import Alert from '../../components/Alert';
import ClearIndicator from '../../components/ClearIndicator';
import DropdownIndicator from '../../components/DropdownIndicator';
import Header from '../../components/Header';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import ModalConfirmation from '../../components/ModalConfirmation';
import ModalFormCsv from '../../components/ModalFormCsv';
import Table from '../../components/Table';
import { AuthContext } from '../../context/authContext';
import { useTableSearch } from '../../hooks/useTableSearch';
import { customStyles } from '../../utils/customStyles';
import { _retrieveLocalStorageItem } from '../../utils/localStorage';
import {
	Outlet,
	TechSupportForm,
	UpdateUsersBody,
	User,
	Users,
} from './Home.type';

const Home = () => {
	const [users, setUsers] = React.useState<Array<Users>>([]);
	const [outlets, setOutlets] = React.useState<Array<Option>>([]);
	const { logout } = React.useContext(AuthContext);
	const [isModalConfirmationVisible, setIsModalConfirmationVisible] =
		React.useState(false);
	const [isEdited, setIsEdited] = React.useState(false);
	const [isSearch, setIsSearch] = React.useState(false);
	const [isModalInputVisible, setIsModalInputVisible] = React.useState(false);
	const [isModalCsvVisible, setIsModalCsvVisible] = React.useState(false);
	const [isLoading, setIsLoading] = React.useState(false);
	const [isLoadingUser, setIsLoadingUser] = React.useState(true);
	const [isAlertVisible, setIsAlertVisible] = React.useState(false);

	const toggleModalConfirmation = () =>
		setIsModalConfirmationVisible(!isModalConfirmationVisible);
	const toggleModalCsv = () => setIsModalCsvVisible(!isModalCsvVisible);
	const toggleModalInput = React.useCallback(
		() => setIsModalInputVisible(!isModalInputVisible),
		[isModalInputVisible],
	);
	const toggleLoading = () => setIsLoading(!isLoading);

	const closeModalInput = () => {
		toggleModalInput();
		setIsAlertVisible(false);
	};

	const validationSchema = Yup.object().shape({
		name: Yup.string().required('Nama tidak boleh kosong'),
		email: Yup.string()
			.email('Format email yang kamu masukkan salah')
			.required('Email tidak boleh kosong'),
		selectedOutlet: Yup.object().shape({
			label: Yup.string().required(),
			value: Yup.string().required(),
		}),
	});

	const {
		values,
		handleBlur,
		handleChange,
		handleSubmit,
		setFieldValue,
		setFieldTouched,
		touched,
		errors,
		resetForm,
	} = useFormik<TechSupportForm>({
		initialValues: {
			userId: null,
			name: '',
			email: '',
			textSearch: '',
			status: false,
			selectedOutlet: null,
			selectedMultiOutlet: [],
			errorMessage: '',
		},
		validateOnMount: true,
		validationSchema,
		onSubmit: async (formValues) => {
			const add: User = {
				name: formValues.name,
				email: formValues.email,
				outletId: formValues.selectedOutlet.value,
				outletName: formValues.selectedOutlet.label,
			};

			const update: UpdateUsersBody = {
				id: formValues.userId,
				outletId: formValues.selectedOutlet.value,
				outletName: formValues.selectedOutlet.label,
				active: formValues.status,
			};

			if (isEdited) {
				await postUpdateUsers(update).then(({ messageDescription, status }) => {
					if (status === 'success') {
						toggleLoading();
						toggleModalInput();
						resetForm({
							values: {
								userId: null,
								name: '',
								email: '',
								textSearch: '',
								status: false,
								selectedOutlet: null,
								selectedMultiOutlet: [],
								errorMessage: '',
							},
						});
						toggleModalConfirmation();
					} else {
						setIsAlertVisible(true);
						toggleLoading();
						setFieldValue('errorMessage', messageDescription);
					}
				});
			} else {
				await postUsers(add).then(({ messageDescription, status }) => {
					if (status === 'success') {
						toggleLoading();
						toggleModalInput();
						resetForm({
							values: {
								userId: null,
								name: '',
								email: '',
								textSearch: '',
								status: false,
								selectedOutlet: null,
								selectedMultiOutlet: [],
								errorMessage: '',
							},
						});
						toggleModalConfirmation();
					} else {
						toggleLoading();
						setIsAlertVisible(true);
						setFieldValue('errorMessage', messageDescription);
					}
				});
			}
		},
	});

	const fetchUsers = React.useCallback(async () => {
		const { data, status } = await getUsers();
		if (status === 'success') {
			setIsLoadingUser(false);
			if (isSearch) {
				return { data };
			} else {
				setUsers(data);
				return { data: [] };
			}
		}
	}, [isSearch]);

	const { filteredData } = useTableSearch({
		searchValue: values.textSearch,
		retrieve: fetchUsers,
	});

	console.log(errors);

	const fetchDetailUsers = React.useCallback(
		async (id: number) => {
			const { data } = await getDetailUsers(id);
			const outlet = {
				label: data[0].outletName,
				value: data[0].otletId,
			};

			const status = data[0].status === 'aktif' ? true : false;
			await setFieldValue('userId', data[0].id);
			await setFieldValue('name', data[0].name, false);
			setFieldTouched('name', true);
			await setFieldValue('email', data[0].email, false);
			setFieldTouched('email', true);
			await setFieldValue('selectedOutlet', outlet);
			await setFieldValue('status', status);
		},
		[setFieldTouched, setFieldValue],
	);

	const fetchOutlets = React.useCallback(async () => {
		const { data, status } = await getOutlets();
		if (status === 'success') {
			const outlets =
				data &&
				data.length > 0 &&
				data.map((item) => {
					return {
						label: item.outletName,
						value: item.outletId,
					};
				});
			setOutlets(outlets);
		}
	}, []);

	const checkAccessLevel = React.useCallback(async () => {
		const accessLevel = await _retrieveLocalStorageItem('accessLevel');
		if (accessLevel !== 'TechSupport') {
			logout();
		}
	}, [logout]);

	React.useEffect(() => {
		checkAccessLevel();
		fetchUsers();
		fetchOutlets();
	}, [checkAccessLevel, fetchOutlets, fetchUsers]);

	const handleEdit = React.useCallback(
		(id) => {
			fetchDetailUsers(id);
			setIsEdited(true);
			toggleModalInput();
		},
		[fetchDetailUsers, toggleModalInput],
	);

	const columns = React.useMemo(
		() => [
			{
				Header: 'Nama',
				accessor: 'name',
				Cell: (row) => <div className="capitalize">{row.value}</div>,
			},
			{
				Header: 'Email',
				accessor: 'email',
			},
			{
				Header: 'Nama Outlet',
				accessor: 'outletName',
			},
			{
				Header: 'Status',
				accessor: 'status',
				Cell: ({ value }) => (
					<div
						className={
							value === 'aktif'
								? 'w-16 py-1.5 px-3 bg-positive-lighter rounded-full'
								: 'w-24 py-1.5 px-3 bg-warning-lighter rounded-full'
						}
					>
						<div
							className={
								value === 'aktif'
									? 'text-positive-default text-center capitalize'
									: 'text-warning-default text-center capitalize'
							}
						>
							{value}
						</div>
					</div>
				),
			},
			{
				Header: 'Aksi',
				accessor: 'action',
				Cell: ({ row }) => (
					<div className="flex justify-center items-center">
						<button
							onClick={() => handleEdit(row.original.id)}
							className="border-2 border-primary-green w-20 rounded-lg py-3 px-4"
						>
							<div className="text-large text-primary-green text-center font-interSemiBold">
								Edit
							</div>
						</button>
					</div>
				),
			},
		],
		[handleEdit],
	);

	const cancelButtonRef = React.useRef(null);

	const closeModalBackground = () => undefined;

	const addUser = () => {
		resetForm({
			values: {
				userId: null,
				name: '',
				email: '',
				textSearch: '',
				status: false,
				selectedOutlet: null,
				selectedMultiOutlet: [],
				errorMessage: '',
			},
		});
		setIsEdited(false);
		toggleModalInput();
	};

	const addUserCSV = () => {
		toggleModalCsv();
	};

	const onChangeOutlet = (selectedOutlet: Outlet) => {
		setFieldValue('selectedOutlet', selectedOutlet);
	};

	const onChangeStatus = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFieldValue('status', event.target.checked);
	};

	const onHandleSubmit = () => {
		toggleLoading();
		handleSubmit();
	};

	const onClick = () => {
		fetchUsers();
		setIsSearch(false);
		toggleModalConfirmation();
	};

	const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		setIsSearch(true);
		setFieldValue('textSearch', event.target.value);
	};

	const onRemoveText = () => {
		setIsSearch(false);
		setFieldValue('textSearch', '');
	};

	const isValid = () => Object.keys(errors).length > 0;

	return (
		<div className="flex flex-col min-h-screen overflow-hidden">
			<ModalConfirmation
				icon="/images/success.svg"
				title={
					isEdited
						? 'Berhasil Mengubah Pengguna'
						: 'Berhasil Menambahkan Pengguna'
				}
				description={
					isEdited
						? 'Kamu berhasil mengubah info pengguna!'
						: 'Kamu berhasil menambahkan pengguna baru!'
				}
				onClick={onClick}
				cancelButtonRef={cancelButtonRef}
				isShowModal={isModalConfirmationVisible}
				closeModalBackground={closeModalBackground}
				variantButton="SINGLE"
				textCenter="Oke, Mengerti"
			/>
			<Modal
				cancelButtonRef={cancelButtonRef}
				isShowModal={isModalInputVisible}
				closeModalBackground={closeModalBackground}
			>
				<div className="mb-6">
					<Dialog.Title
						as="div"
						className="text-left text-extraLarge text-gray-gray700 font-interMedium mb-6"
					>
						Informasi Pengguna
					</Dialog.Title>
					<Alert showAlert={isAlertVisible} text={values.errorMessage} />
					<div className="mb-6">
						<form>
							<div className="mb-4">
								<div className="flex mb-3">
									<label className="text-medium text-gray-gray600 font-interSemiBold uppercase">
										Nama
									</label>
								</div>
								<div
									className={
										touched.name && errors.name
											? 'bg-white w-full h-15 flex justify-center border-2 border-danger-default py-2 px-3 rounded-xl'
											: isEdited
											? 'bg-gray-gray300 w-full h-15 flex justify-center py-2 px-3 rounded-xl'
											: 'bg-white w-full h-15 flex justify-center border border-gray-gray400 py-2 px-3 rounded-xl focus:border-2 focus:border-gray-gray700'
									}
								>
									<input
										autoFocus
										disabled={isEdited ? true : false}
										type="text"
										value={values.name}
										onChange={handleChange('name')}
										onBlur={handleBlur('name')}
										placeholder="Cth: Susanto"
										className={
											isEdited
												? 'w-full text-gray-gray500 laptop:text-large tablet:text-large mobile:text-regular font-interRegular border-0 placeholder-gray-gray400 focus:outline-none focus:ring-transparent'
												: 'w-full text-gray-gray600 laptop:text-large tablet:text-large mobile:text-regular font-interRegular border-0 placeholder-gray-gray400 focus:outline-none focus:ring-transparent'
										}
									/>
								</div>
								{touched.name && errors.name && (
									<div className="text-medium font-interRegular text-danger-default text-left my-4">
										{errors.name}
									</div>
								)}
							</div>
							<div className="mb-4">
								<div className="flex mb-3">
									<label className="text-medium text-gray-gray600 font-interSemiBold uppercase">
										Email
									</label>
								</div>
								<div
									className={
										touched.email && errors.email
											? 'bg-white w-full h-15 flex justify-center border-2 border-danger-default py-2 px-3 rounded-xl'
											: isEdited
											? 'bg-gray-gray300 w-full h-15 flex justify-center py-2 px-3 rounded-xl'
											: 'bg-white w-full h-15 flex justify-center border border-gray-gray400 py-2 px-3 rounded-xl focus:border-2 focus:border-gray-gray700'
									}
								>
									<input
										disabled={isEdited ? true : false}
										type="text"
										value={values.email}
										onChange={handleChange('email')}
										onBlur={handleBlur('email')}
										placeholder="Masukkan email"
										className={
											isEdited
												? 'w-full text-gray-gray500 laptop:text-large tablet:text-large mobile:text-regular font-interRegular border-0 placeholder-gray-gray400 focus:outline-none focus:ring-transparent'
												: 'w-full text-gray-gray600 laptop:text-large tablet:text-large mobile:text-regular font-interRegular border-0 placeholder-gray-gray400 focus:outline-none focus:ring-transparent'
										}
									/>
								</div>
								{touched.email && errors.email && (
									<div className="text-medium font-interRegular text-danger-default text-left my-4">
										{errors.email}
									</div>
								)}
							</div>
							<div className="mb-4">
								<div className="flex mb-3">
									<label className="text-medium text-gray-gray600 font-interSemiBold uppercase">
										Nama Outlet
									</label>
								</div>
								<div>
									<Select
										maxMenuHeight={242}
										styles={customStyles}
										components={{
											DropdownIndicator,
											ClearIndicator,
											IndicatorSeparator: null,
										}}
										isClearable
										menuPortalTarget={document.body}
										name="outlet"
										options={outlets}
										noOp
										placeholder="Cari Outlet"
										value={values.selectedOutlet}
										onChange={onChangeOutlet}
										noOptionsMessage={() => 'Pencarian tidak ditemukan'}
									/>
								</div>
							</div>
							{isEdited && (
								<div className="mb-6">
									<div className="flex items-center">
										<div className="text-large text-gray-gray700 font-interRegular mr-3.5">
											Status Pengguna Aktif
										</div>
										<input
											type="checkbox"
											onChange={onChangeStatus}
											checked={values.status}
										/>
									</div>
								</div>
							)}
						</form>
					</div>
				</div>
				<div className="flex space-x-4">
					<button
						type="button"
						className="w-1/2 p-4 text-center border-2 border-primary-green bg-white rounded-lg focus:outline-none focus:ring-transparent"
						onClick={closeModalInput}
						ref={cancelButtonRef}
					>
						<div className="text-large text-primary-green font-interSemiBold">
							Batal
						</div>
					</button>
					<button
						disabled={isValid() || isLoading}
						type="button"
						className={
							isValid()
								? 'w-1/2 p-4 flex justify-center items-center text-center rounded-lg bg-gray-gray300 focus:outline-none focus:ring-transparent focus:ring-primary-green'
								: 'w-1/2 p-4 flex justify-center items-center text-center rounded-lg bg-primary-green focus:outline-none focus:ring-transparent focus:ring-primary-green cursor-default'
						}
						onClick={onHandleSubmit}
					>
						{isLoading ? (
							<div>
								<img src="/images/spinner.gif" className="w-6 h-6" />
							</div>
						) : (
							<div
								className={
									isValid()
										? 'text-large text-gray-gray500 font-interSemiBold'
										: 'text-large text-white font-interSemiBold'
								}
							>
								Simpan
							</div>
						)}
					</button>
				</div>
			</Modal>
			<ModalFormCsv
				cancelButtonRef={cancelButtonRef}
				showModal={isModalCsvVisible}
				closeModal={toggleModalCsv}
				toggleModalConfirmation={toggleModalConfirmation}
				closeModalBackground={closeModalBackground}
			/>
			<Header title="Yummy StockTake System" />
			<Layout>
				<section className="relative laptop:pt-32 tablet:pt-32 mobile:pt-28 pb-14">
					<div className="flex justify-between mb-6 laptop:mx-36 tablet:mx-10 mobile:mx-4">
						<div className="text-gray-gray700 laptop:text-extraLarge2 tablet:text-extraLarge2 mobile:text-extraLarge font-interMedium mb-6">
							Akses User
						</div>
						<div className="flex">
							<a
								className="flex items-center p-3 text-center rounded-lg
               bg-transparent border-2 border-primary-green font-interSemiBold text-white focus:outline-none focus:ring-transparent focus:ring-primary-green mr-4"
								href={`${process.env.REACT_APP_BASE_URL}/export-user`}
							>
								<div className="text-primary-green text-large font-interSemiBold mr-5 ">
									Ekspor Data
								</div>
								<img src="/images/download.svg" className="w-6 h-6" />
							</a>
							<Menu as="div" className="relative inline-block">
								<Menu.Button
									className="flex items-center p-4 text-center rounded-lg
               bg-primary-green font-interSemiBold text-white focus:outline-none focus:ring-transparent focus:ring-primary-green"
								>
									<div className="text-white text-large font-interSemiBold mr-5 ">
										Tambahkan Pengguna
									</div>
									<img src="images/plus.svg" className="w-6 h-6" />
								</Menu.Button>
								<Transition
									as={React.Fragment}
									enter="transition ease-out duration-100"
									enterFrom="transform opacity-0 scale-95"
									enterTo="transform opacity-100 scale-100"
									leave="transition ease-in duration-75"
									leaveFrom="transform opacity-100 scale-100"
									leaveTo="transform opacity-0 scale-95"
								>
									<Menu.Items className="absolute w-97 right-0 mt-2.5 p-4 hover:p-4 origin-center bg-white rounded-xl shadow-lg focus:outline-none">
										<div className="px-1 py-1">
											<Menu.Item>
												{() => (
													<div>
														<div
															onClick={addUserCSV}
															className="flex justify-center items-center cursor-pointer hover:bg-gray-gray200 hover:rounded-xl hover:p-2 mb-4"
														>
															<img src="/images/tnc.svg" className="mr-5" />
															<div>
																<div className="text-large text-gray-gray700 font-interMedium mb-2">
																	Import CSV
																</div>
																<div className="text-large text-gray-gray500 font-interRegular">
																	Gunakan template CSV untuk mengupload data
																	secara instan
																</div>
															</div>
														</div>
														<div
															onClick={addUser}
															className="flex justify-center items-center cursor-pointer hover:bg-gray-gray200 hover:rounded-xl hover:p-2"
														>
															<img src="/images/pencil.svg" className="mr-5" />
															<div>
																<div className="text-large text-gray-gray700 font-interMedium mb-2">
																	Tambahkan Manual
																</div>
																<div className="text-large text-gray-gray500 font-interRegular">
																	Isi informasi pengguna menggunakan form yang
																	tersedia
																</div>
															</div>
														</div>
													</div>
												)}
											</Menu.Item>
										</div>
									</Menu.Items>
								</Transition>
							</Menu>
						</div>
					</div>
					<div className="bg-white rounded-xl shadow-default p-6 mb-6 laptop:mx-36 tablet:mx-10 mobile:mx-4">
						{users.length > 0 && !isLoadingUser ? (
							<>
								<div className="w-102 relative mr-3 mb-6">
									<input
										value={values.textSearch}
										onChange={onSearch}
										placeholder="Cari nama, outlet, atau email"
										className="w-full flex justify-center items-center bg-white border-gray-gray500 rounded-lg laptop:py-3 tablet:py-3 mobile:py-2 px-10 border focus:border-2 focus:border-gray-gray700 text-gray-gray700 laptop:text-large tablet:text-large mobile:text-regular outline-none font-interRegular placeholder-gray-gray400"
									/>
									{values.textSearch ? (
										<img
											src="/images/searchBlack.svg"
											className="w-6 h-6 absolute mr-3 laptop:-mt-10 tablet:-mt-9 mobile:-mt-7 ml-3"
										/>
									) : (
										<img
											src="/images/search.svg"
											className="absolute mr-3 laptop:-mt-10 tablet:-mt-9 mobile:-mt-7 ml-3"
										/>
									)}
									{values.textSearch && (
										<div
											onClick={onRemoveText}
											className="w-full absolute laptop:right-4 laptop:-mt-9 tablet:right-4 tablet:-mt-9 mobile:-ml-14 mobile:-mt-8 flex justify-end items-end"
										>
											<img src="images/cross.svg" />
										</div>
									)}
								</div>
								<Table
									columns={columns}
									data={isSearch ? filteredData : users}
									pageSize={10}
								/>
							</>
						) : !values.textSearch && users.length === 0 && !isLoadingUser ? (
							<div className="border border-gray-gray300 rounded-xl flex justify-center py-8">
								<div className="w-80 text-extraLarge2 text-gray-gray700 text-center font-interMedium">
									Belum ada data ditampilkan untuk saat ini
								</div>
							</div>
						) : !values.textSearch && isLoadingUser ? (
							<div className="border border-gray-gray300 rounded-xl flex justify-center py-8">
								<div className="w-80 text-extraLarge2 text-gray-gray700 text-center font-interMedium">
									Sedang memuat data...
								</div>
							</div>
						) : null}
					</div>
				</section>
			</Layout>
		</div>
	);
};

export default Home;
